import * as React from 'react';
import { toRelativeUrl } from '@okta/okta-auth-js';
import { LoginCallback, Security, useOktaAuth } from '@okta/okta-react';
import { Routes, Route, Navigate, Outlet, useNavigate } from 'react-router-dom';
import { useActions } from './appActions';
import './app.scss';
import { ElementFn } from '../../elements/elementAction';
import { Functions } from '../../types/types';
import { AuthClient } from '../../utils/authClient';
import URLManager from '../../utils/urlManager';
import { Overlay } from 'rcd-cpp-ui/ui/loader/Overlay';
import { ContentPlanning } from '../ContentPlanning/contentPlanning';
import { SessionExpiry } from './SessionExpiry/sessionExpiry';
import { Home } from '../Home/home';
import ErrorPage from '../ErrorPage/errorPage';
import { CreateWorkspace } from '../CreateWorkspace/createWorkspace';
import { Loader } from '../Loader/loader';
import { Page } from '../Page/page';
import { ItemHome } from '../ItemHome/itemHome';
import { ItemExpiryHome } from '../ItemHome/itemExpiryHome';
import { useFullStory } from './fullStory';
import { useWebSocketActions } from '../../services/webSocketActions';
import { useSelector } from '../../utils/useSelector';
import { FSEvent } from '../../enums/FSEvent';

export const App = ({
  children,
  elFn
}: Pick<React.HTMLAttributes<HTMLDivElement>, 'children'> & {
  elFn: ElementFn<Functions>;
}) => {
  const { init } = useActions();
  React.useEffect(() => {
    init(elFn);
  }, []);
  return <>{children}</>;
};

export const StandaloneApp = ({
  elFn
}: {
  elFn: Pick<ElementFn<Functions>, 'getAPI'>;
}) => {
  const authError = useSelector(s => s.app?.authError);
  const user = useSelector(s => s.app?.user);
  const navigate = useNavigate();
  const { init, getUserInfo } = useActions();
  const { initialize, captureEvent } = useFullStory();
  const { initWebSocket, disconnectWebSocket } = useWebSocketActions();
  React.useEffect(() => {
    init(elFn);
  }, []);
  React.useEffect(() => {
    if (!!user && !!user?.employeeId) {
      initialize();
      (window as any).captureDemandSensingResourcesLink = url => {
        captureEvent(FSEvent.DemandSensingResourcesLink, { url });
      };
    }
  }, [user?.employeeId]);
  React.useEffect(() => {
    if (user?.employeeId) {
      initWebSocket();
    }
    return () => {
      disconnectWebSocket();
    };
  }, [user?.employeeId]);
  return (
    <>
      <Security
        oktaAuth={AuthClient.get()}
        restoreOriginalUri={async (oktaAuth, originalUri) => {
          const origin = toRelativeUrl(
            originalUri || '/',
            window.location.origin
          );
          await getUserInfo();
          navigate(origin);
        }}
        onAuthRequired={() => {
          AuthClient.get().token.getWithRedirect();
        }}
      >
        <Routes>
          {authError && (
            <Route
              path="/auth/callback"
              element={<ErrorPage>{authError}</ErrorPage>}
            />
          )}
          <Route path="/auth/callback/*" element={<AuthCallback />} />
          <Route path="/build-info" element={<BuildInfo />} />
          <Route path="/*" element={<RequiredAuth />}>
            {!!user && (
              <>
                <Route path="" element={<Home />} />
                <Route
                  path="item/expirydashboard?/:refId?"
                  element={<ItemHome />}
                />
                <Route
                  path="item/expirydashboard"
                  element={<ItemExpiryHome />}
                />
                <Route path="w/:wid/*" element={<ContentPlanning />} />
                <Route path="*" element={<Navigate to="/" />} />
                {process.env.ENV != 'prod' && (
                  <Route path="w/create" element={<CreateWorkspace />} />
                )}
              </>
            )}
          </Route>

          {!user && (
            <Route
              path="/*"
              element={
                <Page>
                  <Loader />
                </Page>
              }
            />
          )}
        </Routes>
      </Security>
      <SessionExpiry />
    </>
  );
};

const AuthCallback = () => {
  const { setAuthError } = useActions();
  const navigate = useNavigate();
  if (URLManager.isAuthCallbackOnly()) {
    return null;
  }
  return (
    <LoginCallback
      errorComponent={({ error }) => {
        setAuthError(error.message);
        if (error.name == 'AuthSdkError') {
          navigate('/auth/callback');
        }
        return null;
      }}
    />
  );
};

const BuildInfo = () => {
  const { ENV, VERSION, BUILT_TIME } = process.env;
  return <pre>{JSON.stringify({ ENV, VERSION, BUILT_TIME }, null, 4)}</pre>;
};

const RequiredAuth = () => {
  const { oktaAuth, authState } = useOktaAuth();

  React.useEffect(() => {
    if (!authState) {
      return;
    }

    if (!authState?.isAuthenticated) {
      const originalUri = toRelativeUrl(
        window.location.href,
        window.location.origin
      );
      oktaAuth.setOriginalUri(originalUri);
      oktaAuth.signInWithRedirect();
    }
  }, [oktaAuth, !!authState, authState?.isAuthenticated]);

  if (!authState || !authState?.isAuthenticated) {
    return <Overlay />;
  }

  return <Outlet />;
};
